// **React Imports
import React, { memo, useCallback, useMemo } from 'react'

// **MUI Imports
import { Box, Typography } from '@mui/material'

// **Third Party Imports
import DatePicker from 'react-datepicker'
import { Controller } from 'react-hook-form'

// **Component Imports
import RequiredLabelComponent from '../required-label-component'

// **library css import
import 'react-datepicker/dist/react-datepicker.css'

// **SCSS Imports
import './index.scss'

const CustomDateTimePicker = ({
  label,
  name,
  value,
  control,
  placeholder,
  tooltipText,
  required = false,
  timeIntervals = 5,
  onChange = () => {},
  showTimeSelect = true,
  usedInHookForm = true,
  allowPreviousDate = false,
  minDate: inputMinDate,
  ...rest
}) => {
  // Variable
  const minDate = useMemo(() => {
    return inputMinDate ? new Date(inputMinDate) : null
  }, [inputMinDate])

  // handler to filter passed time
  const filterPassedTime = useCallback(
    (time) => {
      const currentDate = minDate || new Date()
      const selectedDate = new Date(time)

      if (selectedDate.getDate() === currentDate.getDate()) {
        const currentHours = currentDate.getHours()
        const currentMinutes = currentDate.getMinutes()

        const selectedHours = selectedDate.getHours()
        const selectedMinutes = selectedDate.getMinutes()

        // Compare hours and minutes only
        if (
          selectedHours > currentHours ||
          (selectedHours === currentHours && selectedMinutes >= currentMinutes)
        ) {
          return true
        }
        return false
      }
      return true
    },
    [minDate]
  )

  const roundToNearestInterval = useCallback((date, interval) => {
    const minutes = date.getMinutes()
    const roundedMinutes = Math.ceil(minutes / interval) * interval
    date.setMinutes(roundedMinutes)
    date.setSeconds(0)
    date.setMilliseconds(0)
    return date
  }, [])

  const handleDateChange = useCallback(
    (selectedDate) => {
      if (!selectedDate) {
        return null
      }

      const now = minDate || new Date()
      let dateToUpdate

      // If previous dates are not allowed and selected date-time is in the past
      if (!allowPreviousDate && selectedDate <= now) {
        dateToUpdate = roundToNearestInterval(now, timeIntervals)
      } else {
        dateToUpdate = selectedDate
      }
      return dateToUpdate
    },
    [allowPreviousDate, minDate, roundToNearestInterval, timeIntervals]
  )

  return (
    <Box className={`date-time-picker-wrapper ${rest?.error ? 'error' : ''}`}>
      {label && (
        <RequiredLabelComponent
          label={label}
          error={rest?.error}
          required={required}
          tooltipText={tooltipText}
        />
      )}
      {usedInHookForm ? (
        <Controller
          name={name}
          control={control}
          render={({ field: { value, onChange, onBlur } }) => (
            <DatePicker
              selected={value ? new Date(value) : null}
              onChange={(value) => onChange(handleDateChange(value))}
              onBlur={onBlur}
              placeholderText={placeholder}
              minDate={allowPreviousDate ? null : minDate || new Date()}
              timeFormat="HH:mm"
              showTimeSelect={showTimeSelect}
              onKeyDown={(e) => {
                if (e.key !== 'Backspace') {
                  e.preventDefault()
                }
              }}
              timeIntervals={timeIntervals}
              dateFormat={showTimeSelect ? 'dd-MMM-yyyy HH:mm' : 'dd-MMM-yyyy'}
              filterTime={allowPreviousDate ? null : filterPassedTime}
              className="date-time-picker-field"
              {...rest}
            />
          )}
        />
      ) : (
        <DatePicker
          selected={value ? new Date(value) : null}
          minDate={allowPreviousDate ? null : minDate || new Date()}
          timeFormat="HH:mm"
          placeholderText={placeholder}
          showTimeSelect={showTimeSelect}
          timeIntervals={timeIntervals}
          dateFormat={showTimeSelect ? 'dd-MMM-yyyy HH:mm' : 'dd-MMM-yyyy'}
          onKeyDown={(e) => {
            if (e.key !== 'Backspace') {
              e.preventDefault()
            }
          }}
          filterTime={allowPreviousDate ? null : filterPassedTime}
          className="date-time-picker-field"
          onChange={(value) => onChange(handleDateChange(value))}
          {...rest}
        />
      )}
      {rest?.error && rest?.helperText && (
        <Typography className="error-text" mt={1}>
          {rest?.helperText}
        </Typography>
      )}
    </Box>
  )
}

export default memo(CustomDateTimePicker)
